import { useEffect, useLayoutEffect, useRef } from 'react'

const API_ENDPOINT = import.meta.env.VITE_API_ENDPOINT as string
const PUBSUB_WS_URL = (import.meta.env.VITE_PUBSUB_URL as string).replace('https://', 'wss://')

const MIN_DELAY_MS = 1_000
const MAX_DELAY_MS = 30_000

// JWT do app: chega na URL (?token=) ou no localStorage (@token).
function getJwt(): string | null {
  try {
    const params = new URLSearchParams(window.location.search)
    return params.get('token') ?? window.localStorage.getItem('@token')
  } catch {
    return null
  }
}

// O pubsub exige um subscribe-token curto assinado (?token=) no upgrade do WS.
// O webview não tem o PUBSUB_SECRET: troca o JWT do app por um token curto no
// gateway (POST /pubsub-token). Cache de módulo (TTL 300s; guardamos ~250s) evita
// 1 request por subscription. `inflight` deduplica chamadas paralelas.
let cachedToken: { token: string; exp: number } | null = null
let inflight: Promise<string | null> | null = null

async function getSubscribeToken(): Promise<string | null> {
  if (cachedToken && cachedToken.exp > Date.now()) return cachedToken.token
  if (inflight) return inflight
  inflight = (async () => {
    try {
      const jwt = getJwt()
      if (!jwt) return null
      const res = await fetch(`${API_ENDPOINT}/pubsub-token`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${jwt}` },
      })
      if (!res.ok) return null
      const data = await res.json()
      if (data?.token) {
        cachedToken = { token: data.token, exp: Date.now() + 250_000 }
        return data.token
      }
      return null
    } catch {
      return null
    } finally {
      inflight = null
    }
  })()
  return inflight
}

interface Props<T> {
  endpoint: string
  skip?: boolean
  onData: (data: T) => void
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useSub = <T = Record<string, any>>({
  endpoint,
  skip,
  onData,
}: Props<T>) => {
  const memoizedFunction = useRef(onData)

  useLayoutEffect(() => {
    memoizedFunction.current = onData
  }, [onData])

  useEffect(() => {
    if (skip) return

    let ws: WebSocket
    let retryTimer: ReturnType<typeof setTimeout>
    let delay = MIN_DELAY_MS
    let destroyed = false

    async function connect() {
      if (destroyed) return

      // token curto pro WS; se falhar, conecta sem (pubsub sem P1 aceita).
      const subToken = await getSubscribeToken()
      if (destroyed) return
      const qs = subToken ? `?token=${encodeURIComponent(subToken)}` : ''

      ws = new WebSocket(`${PUBSUB_WS_URL}${endpoint}${qs}`)

      ws.onmessage = ({ data }) => {
        try {
          const parsed = JSON.parse(data as string)
          memoizedFunction.current(parsed.data ?? parsed)
        } catch {}
        delay = MIN_DELAY_MS
      }

      ws.onerror = () => {}

      ws.onclose = () => {
        if (destroyed) return
        // token pode ter expirado → limpa o cache e busca fresco na retentativa.
        cachedToken = null
        retryTimer = setTimeout(() => {
          if (!destroyed) void connect()
        }, delay)
        delay = Math.min(delay * 2, MAX_DELAY_MS)
      }
    }

    void connect()

    return () => {
      destroyed = true
      clearTimeout(retryTimer)
      ws?.close()
    }
  }, [endpoint, skip])
}
